<template>
    <div class="h-full overflow-hidden flex flex-col relative mx-auto"
         :class="{ 'flex-1': !openDetail }"
         :style="computedStyle"
    >
        <slot name="header"></slot>
        <div class="agent-panel-scroll-container custom-scrollbar overflow-auto grow">
            <div
                class="w-full py-10 px-4 flex-1 mx-auto -mt-10 sm:mt-0"
            >
                <transition-group
                    name="message-list"
                    tag="div"
                    class="flex flex-col gap-4"
                >
                    <div
                        v-for="(message, index) in _messages"
                        :key="message?.content?.message_group || index"
                        class="message-item gap-4 flex flex-col"
                    >
                        <WelcomeMessage v-if="message.type === COMPONENT_TYPE.WELCOME" :data="message.content" />
                        <SelfMessage v-if="message.type === COMPONENT_TYPE.USER" :data="message.content" />
                        <!-- 标题 -->
                        <div class="flex justify-between items-center gap-2" v-if="_messages[index - 1]?.type === COMPONENT_TYPE.USER">
                            <div class="flex items-center gap-2">
                                <PtIcon icon="Copilot" class="text-2xl shrink-0" />
                                <span v-safe-html="title" class="font-semibold"/>
                            </div>
                        </div>
                        <BotMessage v-if="message.type === COMPONENT_TYPE.AGENT" :data="message.content" :messages="_messages" :isLastAgentMessage="index === _messages.length - 1"/>
                        <InterruptedMessage v-if="message.type === COMPONENT_TYPE.INTERRUPTED" :data="message.content" />
                    </div>
                </transition-group>
            </div>
        </div>

        <div class="shrink-0 z-10 w-full">
            <TrizInputContainer />
        </div>

        <div
            v-if="openDetail"
            class="resize-handle absolute right-0 top-0 h-full bg-grey-40 cursor-col-resize hover:w-1 hover:bg-eureka-500 active:bg-eureka-600 z-[11]"
            @mousedown="startResize"
        ></div>
    </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeMount } from 'vue';
import { COMPONENT_TYPE } from '@/domains/chat/types/triz-component'
import { SSE_EVENT_TYPE, type WelcomeMessageData } from '@/domains/chat/types/triz-events'
import WelcomeMessage from './message-card/WelcomeMessage.vue'
import { useMessages } from '@/composables/chat/use-messages';
import '@/assets/icon/copilot.svg';
import { useI18n } from 'vue-i18n';
import { PtIcon } from 'patsnap-biz'
import { useResizePanel } from '@/composables/chat/use-resize-panel';

// 🚀 性能优化：懒加载非首屏必需的消息组件，减少首屏 bundle
const BotMessage = defineAsyncComponent(() => import('./message-card/BotMessage.vue'));
const SelfMessage = defineAsyncComponent(() => import('./message-card/SelfMessage.vue'));
const InterruptedMessage = defineAsyncComponent(() => import('./message-card/InterruptedMessage.vue'));
const TrizInputContainer = defineAsyncComponent(() => import('./TrizInputContainer.vue'));

const { openDetail, messages } = useMessages();

const _messages = computed(() => messages.value.filter((m, i) => {
    if (m.type === COMPONENT_TYPE.AGENT) {
        if (m.content.confirm_data && !m.content.agent_confirm_data && !m.content.agent_message && i !== messages.value.length - 1) {
            return false
        }
    }
    return true
}))
const { t } = useI18n()

const title = computed(() => {
    return t('chat.BotMessage.defaultTitle')
})

const CHAT_PANEL_MIN_WIDTH = 30; // 最小宽度百分比
const CHAT_PANEL_MAX_WIDTH = 65; // 最大宽度百分比
const CHAT_PANEL_DEFAULT_WIDTH = 38.2; // 默认宽度百分比
const CHAT_PANEL_FULL_WIDTH = 760; // 无详情面板时的固定宽度（px）

const { currentWidth, startResize } = useResizePanel({
    minWidth: CHAT_PANEL_MIN_WIDTH,
    maxWidth: CHAT_PANEL_MAX_WIDTH,
    initialWidth: CHAT_PANEL_DEFAULT_WIDTH,
    direction: 'left',
});

// 计算样式 - 左侧聊天面板始终显示，只在有详情面板时调整宽度
const computedStyle = computed(() => {
    if (openDetail.value) {
        return {
            width: `${currentWidth.value}%`,
            maxWidth: `${CHAT_PANEL_MAX_WIDTH}%`,
            minWidth: `${CHAT_PANEL_MIN_WIDTH}%`,
        };
    }
    // 没有详情面板时，聊天面板使用固定宽度并居中
    return {
        width: `${CHAT_PANEL_FULL_WIDTH}px`,
        maxWidth: `${CHAT_PANEL_FULL_WIDTH}px`,
    };
});
// 组件挂载时立即推入欢迎消息，实现零延迟首屏渲染
onBeforeMount(() => {
    // 检查是否已经存在欢迎消息（避免重复添加）
    const hasWelcome = messages.value.some(m => m.type === COMPONENT_TYPE.WELCOME);
    if (!hasWelcome) {
        messages.value.push({
            type: COMPONENT_TYPE.WELCOME,
            content: {
                id: 0,
                business_id: '',
                data: {
                    message_type: SSE_EVENT_TYPE.WELCOME,
                },
            } as WelcomeMessageData,
        });
    }
});

</script>

<style scoped lang="scss">
.message-list-enter-active,
.message-list-leave-active {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.message-list-enter-from {
    opacity: 0;
    transform: translateY(20px);
}

.message-list-leave-to {
    opacity: 0;
    transform: translateY(-20px);
}

.message-list-move {
    transition: transform 0.2s ease;
}

.message-item {
    transform-origin: top;
}

.resize-handle {
  position: absolute;
  right: 0;
  top: 0;
  width: 1px;
  height: 100%;
  cursor: col-resize;
  transition: background-color 0.2s;
}
</style>
